


































import Vue from 'vue';
import Component from 'vue-class-component';
import VueApexCharts from 'vue-apexcharts';
import moment from 'moment';
import { translateMonths } from '@/modules/shared/shared/domain/functions/date-functions';
import { ticketsDashboard } from '@/modules/shared/shared/store/namespaces';
import { TicketStatus } from '@/modules/shared/shared/domain/constants/constants';

@Component({
    components: {
        apexchart: VueApexCharts,
    },
})
export default class TicketsByStatus extends Vue {
    @ticketsDashboard.State('tickets') tickets;

    get isLoading(): boolean {
        return !this.tickets;
    }

    get chartType(): string {
        return 'area';
    }

    get chartOptions() {
        const fontFamily = 'Roboto, sans-serif';

        let config: object = {
            chart: {
                defaultLocale: 'es',
                type: this.chartType,
                fontFamily,
                toolbar: {
                    show: false,
                    tools: {
                        download: false,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: false,
                        reset:
                            '<svg xmlns="http://www.w3.org/2000/svg" version="1.1" x="0px" y="0px" viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve" width="16px" height="16px"><path d="M511.957,185.214L512,15.045l-67.587,67.587l-7.574-7.574c-48.332-48.332-112.593-74.95-180.946-74.95    C114.792,0.107,0,114.901,0,256s114.792,255.893,255.893,255.893S511.785,397.099,511.785,256h-49.528    c0,113.79-92.575,206.365-206.365,206.365S49.528,369.79,49.528,256S142.103,49.635,255.893,49.635    c55.124,0,106.947,21.467,145.925,60.445l7.574,7.574l-67.58,67.58L511.957,185.214z" data-original="#000000" class="active-path" data-old_color="#000000" fill="#6E8192"/></svg>',
                    },
                },
                locales: [
                    {
                        name: 'es',
                        options: {
                            toolbar: {
                                selection: 'Selección',
                                selectionZoom: 'Zoom de selección',
                                zoomIn: 'Acercar',
                                zoomOut: 'Alejar',
                                reset: 'Reestablecer',
                            },
                        },
                    },
                ],
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                show: true,
                position: 'bottom',
                horizontalAlign: 'center',
            },
            fill: {
                gradient: {
                    enabled: true,
                    opacityFrom: 0.55,
                    opacityTo: 0,
                },
            },
            stroke: {
                width: 3,
                curve: 'smooth',
            },
            grid: {
                borderColor: '#9598a6',
            },
            xaxis: {
                categories: [],
            },
        };

        if (!this.isLoading) {
            config = {
                ...config,
                colors: this.chartData.series.map(serie => serie.color),
                xaxis: {
                    categories: this.chartData.categories,
                    labels: {
                        rotate: -45,
                        rotateAlways: true,
                        style: {
                            fontSize: '0.75rem',
                        },
                    },
                },
                yaxis: {
                    labels: {
                        style: { fontSize: '0.75rem' },
                    },
                    title: {
                        text: 'Cantidad de Tickets',
                    },
                },
                tooltip: {
                    enabled: true,
                    x: {
                        show: true,
                        formatter: (val: string, series: any): string => {
                            let tooltip: string = val;

                            try {
                                const sIdx =
                                    series.w.config.series[series.seriesIndex].indices[
                                        series.dataPointIndex
                                    ];

                                tooltip = moment(sIdx, 'M-YYYY')
                                    .locale('es')
                                    .format('MMMM YYYY');
                            } catch (err) {
                                // Nada por los momentos...
                            }

                            return tooltip;
                        },
                    },
                },
            };
        }

        return config;
    }

    public months = 12;

    getChartCategories(monthsFromNow): { display: string; month: number; year: number }[] {
        let categories: { display: string; month: number; year: number }[] = [];
        const dateStart = moment()
            .subtract(monthsFromNow - 1, 'month')
            .locale('es');

        while (monthsFromNow-- > 0) {
            categories.push({
                display: dateStart.format('MMMM YYYY'),
                month: parseInt(dateStart.format('M')),
                year: parseInt(dateStart.format('YYYY')),
            });
            dateStart.add(1, 'month');
        }

        return categories;
    }

    get chartData(): {
        categories: string[];
        series: { name: string; data: number[]; indices: string[]; color: string }[];
    } {
        const result: {
            categories: string[];
            series: { name: string; data: number[]; indices: string[]; color: string }[];
        } = {
            categories: [],
            series: [],
        };

        try {
            if (!this.isLoading) {
                const categories = this.getChartCategories(this.months);

                this.tickets.values.ticketsStatusRecord.forEach(item => {
                    let idx = result.series.findIndex(serie => {
                        return serie.name === item.status;
                    });

                    if (idx === -1) {
                        const data: number[] = new Array(categories.length);
                        idx = result.series.push({
                            name: item.status,
                            data: data.fill(0),
                            indices: categories.map(category => {
                                return `${category.month}-${category.year}`;
                            }),
                            color: '#000',
                        });
                        idx--;
                    }

                    const dataIdx = result.series[idx].indices.indexOf(
                        `${item.monthNumber}-${item.year}`,
                    );

                    if (dataIdx !== -1) {
                        result.series[idx].data[dataIdx] = item.total;
                    }

                    switch (result.series[idx].name.toLowerCase()) {
                        case 'solucionado':
                            result.series[idx].color = '#ffc107';
                            break;
                        case 'cerrado':
                            result.series[idx].color = '#00bcd4';
                            break;
                        case 'abierto':
                        default:
                            result.series[idx].color = '#e91e63';
                            break;
                    }
                });

                result.categories = categories.map(category => {
                    return moment()
                        .month(category.month - 1)
                        .locale('es')
                        .format('MMM');
                });
            }
        } catch (err) {
            // Nada por los momentos...
            //console.error(err);
        }

        return result;
    }

    //get chartSeries() {
    // let historyData: { name: string; data: number[] }[] = [
    //     {
    //         name: TicketStatus.OPENED,
    //         data: [],
    //     },
    //     {
    //         name: TicketStatus.FIXED,
    //         data: [],
    //     },
    //     {
    //         name: TicketStatus.CLOSED,
    //         data: [],
    //     },
    // ];
    // if (this.tickets && this.tickets.values !== undefined) {
    //     let auxCategories: string[] = this.getDataCategory();
    //     for (let index = 0; index < auxCategories.length; index++) {
    //         for (let indx = 0; indx < this.tickets.values.ticketsStatusRecord.length; indx++) {
    //             if (
    //                 auxCategories[index] ==
    //                 translateMonths(this.tickets.values.ticketsStatusRecord[indx].monthName)
    //             ) {
    //                 if (
    //                     this.tickets.values.ticketsStatusRecord[indx].status ==
    //                     TicketStatus.OPENED
    //                 ) {
    //                     if (historyData[0].data[index] === undefined) {
    //                         historyData[0].data.push(
    //                             this.tickets.values.ticketsStatusRecord[indx].total,
    //                         );
    //                     } else {
    //                         historyData[0].data.splice(
    //                             index,
    //                             1,
    //                             this.tickets.values.ticketsStatusRecord[indx].total,
    //                         );
    //                     }
    //                     if (historyData[1].data[index] === undefined) {
    //                         historyData[1].data.push(0);
    //                     }
    //                     if (historyData[2].data[index] === undefined) {
    //                         historyData[2].data.push(0);
    //                     }
    //                 } else if (
    //                     this.tickets.values.ticketsStatusRecord[indx].status ==
    //                     TicketStatus.FIXED
    //                 ) {
    //                     if (historyData[1].data[index] === undefined) {
    //                         historyData[1].data.push(
    //                             this.tickets.values.ticketsStatusRecord[indx].total,
    //                         );
    //                     } else {
    //                         historyData[1].data.splice(
    //                             index,
    //                             1,
    //                             this.tickets.values.ticketsStatusRecord[indx].total,
    //                         );
    //                     }
    //                     if (historyData[0].data[index] === undefined) {
    //                         historyData[0].data.push(0);
    //                     }
    //                     if (historyData[2].data[index] === undefined) {
    //                         historyData[2].data.push(0);
    //                     }
    //                 } else {
    //                     if (historyData[2].data[index] === undefined) {
    //                         historyData[2].data.push(
    //                             this.tickets.values.ticketsStatusRecord[indx].total,
    //                         );
    //                     } else {
    //                         historyData[2].data.splice(
    //                             index,
    //                             1,
    //                             this.tickets.values.ticketsStatusRecord[indx].total,
    //                         );
    //                     }
    //                     if (historyData[0].data[index] === undefined) {
    //                         historyData[0].data.push(0);
    //                     }
    //                     if (historyData[1].data[index] === undefined) {
    //                         historyData[1].data.push(0);
    //                     }
    //                 }
    //             }
    //         }
    //     }
    // }
    // return historyData;
    //}
}
